import { useEffect, useMemo, useRef, useState } from 'react';
import api from '../services/api';
import SEO from '../components/SEO';
import { roleLabel, visibleToUser } from '../utils/rbacDefinitions';
import FolderPickerModal from '../components/FolderPickerModal';

const PAGE_SIZE = 8;

/* ── Add User Modal with typeahead (live search) ── */
const ASSIGNABLE_ROLES = [
  { value: 'student',       label: 'Student' },
  { value: 'teacher',       label: 'Teacher' },
  { value: 'college_admin', label: 'College Admin' },
];

const AddUserModal = ({ college, currentUserRole, onAdd, onClose }) => {
  const [query, setQuery] = useState('');
  const [matches, setMatches] = useState([]);
  const [selected, setSelected] = useState(null);
  const [role, setRole] = useState('student');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);

  // Live search via API as the user types
  useEffect(() => {
    if (query.trim().length < 2) { setMatches([]); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await api.get(`/users/search?q=${encodeURIComponent(query.trim())}`);
        // Filter out users already in this college
        setMatches(res.data.filter(u => !(u.colleges || []).includes(college.name)).slice(0, 8));
      } catch (_) { setMatches([]); }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [query, college.name]);

  const pick = (u) => {
    setSelected(u);
    setQuery(u.name);
    setOpen(false);
    const assignable = ASSIGNABLE_ROLES.map(r => r.value);
    setRole(assignable.includes(u.role) ? u.role : 'student');
  };

  const availableRoles = currentUserRole === 'super_user'
    ? ASSIGNABLE_ROLES
    : ASSIGNABLE_ROLES.filter(r => r.value !== 'college_admin');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selected) { setError('Please select a user from the dropdown'); return; }
    setSaving(true); setError('');
    try {
      await onAdd(college, selected.email, selected._id, role);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add user');
    } finally {
      setSaving(false);
    }
  };

  const roleChanged = selected && selected.role !== role;

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(15,23,42,0.55)',
        backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center',
        justifyContent: 'center', padding: '20px' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: 'var(--bg-card)', borderRadius: '16px', padding: '32px', width: '100%',
        maxWidth: '440px', boxShadow: 'var(--shadow-xl)', animation: 'fadeInUp 0.25s ease', border: '1px solid var(--border-light)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: 'var(--text-main)' }}>Add User to College</h3>
            <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: 'var(--text-muted)' }}>{college.name}</p>
          </div>
          <button type="button" onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.3rem', color: '#94a3b8' }}>✕</button>
        </div>

        {error && <div className="error-message" style={{ marginBottom: '14px' }}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', fontSize: '0.875rem', color: '#374151' }}>
            Search by name or email
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="Type at least 2 characters…"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setSelected(null); setOpen(true); }}
              onFocus={() => setOpen(true)}
              onBlur={() => setTimeout(() => setOpen(false), 150)}
              autoFocus
              style={{ width: '100%', boxSizing: 'border-box', background: 'var(--bg-card)', color: 'var(--text-main)' }}
            />
            {open && matches.length > 0 && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
                background: 'var(--bg-card)', border: '1px solid var(--border-light)',
                boxShadow: 'var(--shadow-lg)', marginTop: '4px', overflow: 'hidden', borderRadius: '10px' }}>
                {matches.map(u => (
                  <div key={u._id} onMouseDown={() => pick(u)}
                    style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border-light)',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg-card)' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-main)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'var(--bg-card)'}
                  >
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '0.875rem', color: 'var(--text-main)' }}>{u.name}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{u.email}{u.colleges?.length ? ` · ${u.colleges.join(', ')}` : ' · No college'}</div>
                    </div>
                    <span style={{ fontSize: '0.68rem', fontWeight: '700', padding: '2px 8px',
                      borderRadius: '10px', background: 'rgba(79, 70, 229, 0.1)', color: 'var(--primary)' }}>
                      {u.role?.replace(/_/g, ' ')}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {selected && (
            <>
              {/* Selected user confirmation */}
              <div style={{ marginTop: '12px', padding: '10px 14px', borderRadius: '10px',
                background: 'rgba(16, 185, 129, 0.1)', border: '1px solid var(--success)', display: 'flex', gap: '10px', alignItems: 'center' }}>
                <i className="fas fa-user-check" style={{ color: 'var(--success)' }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', fontSize: '0.875rem', color: 'var(--text-main)' }}>{selected.name}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{selected.email}</div>
                </div>
              </div>

              {/* Role selector */}
              <div style={{ marginTop: '14px' }}>
                <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', fontSize: '0.875rem', color: '#374151' }}>
                  Assign Role
                </label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {availableRoles.map(r => (
                    <button key={r.value} type="button" onClick={() => setRole(r.value)}
                      style={{ padding: '6px 14px', borderRadius: '20px', border: '2px solid',
                        cursor: 'pointer', fontWeight: '600', fontSize: '0.8rem', transition: 'all 0.15s',
                        borderColor: role === r.value ? 'var(--primary)' : 'var(--border-light)',
                        background: role === r.value ? 'rgba(79, 70, 229, 0.1)' : 'var(--bg-main)',
                        color: role === r.value ? 'var(--primary)' : 'var(--text-muted)' }}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
                {roleChanged && (
                  <p style={{ margin: '6px 0 0', fontSize: '0.75rem', color: '#f59e0b' }}>
                    ⚠️ Current role is <strong>{selected.role?.replace(/_/g, ' ')}</strong> — will be changed to <strong>{role?.replace(/_/g, ' ')}</strong>
                  </p>
                )}
              </div>
            </>
          )}

          <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
            <button type="submit" disabled={saving || !selected} style={{ flex: 1 }}>
              {saving ? 'Saving…' : roleChanged ? 'Add & Update Role' : 'Add to College'}
            </button>
            <button type="button" className="secondary" onClick={onClose} style={{ flex: 1 }}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ── Modal for managing folder access ── */
const FolderAccessModal = ({ folder, allMembers, allGroups, onSave, onClose }) => {
  const [selectedGroups, setSelectedGroups] = useState(folder.allowedGroups || []);
  const [selectedUsers, setSelectedUsers] = useState(folder.allowedUsers || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await onSave(folder._id, { allowedGroups: selectedGroups, allowedUsers: selectedUsers });
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save access');
    } finally {
      setSaving(false);
    }
  };

  const toggleGroup = (id) => setSelectedGroups(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const toggleUser = (id) => setSelectedUsers(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px'
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: 'var(--bg-card)', borderRadius: '16px', padding: '32px',
        width: '100%', maxWidth: '480px', maxHeight: '90vh', overflowY: 'auto',
        boxShadow: 'var(--shadow-xl)', border: '1px solid var(--border-light)',
        animation: 'fadeInUp 0.25s ease'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '700', color: 'var(--text-main)' }}>Manage Access</h3>
            <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: 'var(--text-muted)' }}>{folder.title}</p>
          </div>
          <button
            type="button" onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.3rem', color: '#94a3b8', padding: '4px 8px' }}
          >✕</button>
        </div>

        {error && <div className="error-message" style={{ marginBottom: '16px' }}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', color: 'var(--text-main)', fontSize: '0.875rem' }}>
              Assign to Groups ({selectedGroups.length})
            </label>
            <div style={{ border: '1px solid var(--border-light)', borderRadius: '12px', maxHeight: '240px', overflowY: 'auto', padding: '8px', background: 'var(--bg-main)', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {allGroups.length === 0 ? <div style={{ padding: '20px', color: 'var(--text-muted)', fontSize: '0.9rem', textAlign: 'center' }}>No groups available.</div> : allGroups.map(g => {
                const isSelected = selectedGroups.includes(g._id);
                return (
                  <label key={g._id} style={{ display: 'flex', alignItems: 'center', padding: '10px 14px', cursor: 'pointer', borderRadius: '8px', background: isSelected ? 'rgba(79, 70, 229, 0.1)' : 'var(--bg-card)', border: `1px solid ${isSelected ? 'var(--primary)' : 'var(--border-light)'}`, transition: 'all 0.2s ease', boxShadow: isSelected ? 'var(--shadow-sm)' : 'none' }}>
                    <input type="checkbox" checked={isSelected} onChange={() => toggleGroup(g._id)} style={{ display: 'none' }} />
                    <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: isSelected ? 'var(--primary)' : 'var(--bg-main)', color: isSelected ? '#fff' : 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', marginRight: '14px', flexShrink: 0 }}>
                      <i className="fas fa-users" />
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontWeight: '600', fontSize: '0.95rem', color: isSelected ? '#1e3a8a' : '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{g.name}</div>
                    </div>
                    <div style={{ width: '22px', height: '22px', borderRadius: '50%', border: `2px solid ${isSelected ? '#3b82f6' : '#cbd5e1'}`, background: isSelected ? '#3b82f6' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.2s ease' }}>
                      {isSelected && <i className="fas fa-check" style={{ color: '#fff', fontSize: '0.7rem' }} />}
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', color: 'var(--text-main)', fontSize: '0.875rem' }}>
              Assign to Users ({selectedUsers.length})
            </label>
            <div style={{ border: '1px solid var(--border-light)', borderRadius: '12px', maxHeight: '240px', overflowY: 'auto', padding: '8px', background: 'var(--bg-main)', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {allMembers.length === 0 ? <div style={{ padding: '20px', color: 'var(--text-muted)', fontSize: '0.9rem', textAlign: 'center' }}>No members available.</div> : allMembers.map(u => {
                const isSelected = selectedUsers.includes(u._id);
                return (
                  <label key={u._id} style={{ display: 'flex', alignItems: 'center', padding: '10px 14px', cursor: 'pointer', borderRadius: '8px', background: isSelected ? 'rgba(79, 70, 229, 0.1)' : 'var(--bg-card)', border: `1px solid ${isSelected ? 'var(--primary)' : 'var(--border-light)'}`, transition: 'all 0.2s ease', boxShadow: isSelected ? 'var(--shadow-sm)' : 'none' }}>
                    <input type="checkbox" checked={isSelected} onChange={() => toggleUser(u._id)} style={{ display: 'none' }} />
                    <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: isSelected ? 'var(--primary)' : 'var(--border-light)', color: isSelected ? '#fff' : 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '1rem', marginRight: '14px', flexShrink: 0 }}>
                      {u.name.charAt(0).toUpperCase()}
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontWeight: '600', fontSize: '0.95rem', color: isSelected ? 'var(--primary)' : 'var(--text-main)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.name}</div>
                      <div style={{ fontSize: '0.8rem', color: isSelected ? 'var(--primary)' : 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.email}</div>
                    </div>
                    <div style={{ width: '22px', height: '22px', borderRadius: '50%', border: `2px solid ${isSelected ? 'var(--primary)' : 'var(--border-light)'}`, background: isSelected ? 'var(--primary)' : 'var(--bg-card)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.2s ease' }}>
                      {isSelected && <i className="fas fa-check" style={{ color: '#fff', fontSize: '0.7rem' }} />}
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button type="submit" disabled={saving} style={{ flex: 1 }}>{saving ? 'Saving…' : 'Save Access'}</button>
            <button type="button" className="secondary" onClick={onClose} style={{ flex: 1 }}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ── Modal for creating or editing a college ── */
const CollegeModal = ({ initial, onSave, onClose, title }) => {
  const [form, setForm] = useState(initial || { name: '', code: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save college');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px'
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: 'var(--bg-card)', borderRadius: '16px', padding: '32px',
        width: '100%', maxWidth: '480px',
        boxShadow: 'var(--shadow-xl)', border: '1px solid var(--border-light)',
        animation: 'fadeInUp 0.25s ease'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '700', color: 'var(--text-main)' }}>{title}</h3>
          <button
            type="button" onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.3rem', color: '#94a3b8', padding: '4px 8px' }}
          >✕</button>
        </div>

        {error && <div className="error-message" style={{ marginBottom: '16px' }}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', color: 'var(--text-main)', fontSize: '0.875rem' }}>
              College Name <span style={{ color: 'var(--danger)' }}>*</span>
            </label>
            <input
              placeholder="e.g. Sunrise Engineering College"
              value={form.name}
              onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
              required
              style={{ width: '100%', boxSizing: 'border-box' }}
            />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', color: '#374151', fontSize: '0.875rem' }}>
              Code
            </label>
            <input
              placeholder="e.g. SEC"
              value={form.code}
              onChange={(e) => setForm(prev => ({ ...prev, code: e.target.value }))}
              style={{ width: '100%', boxSizing: 'border-box' }}
            />
          </div>
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', color: '#374151', fontSize: '0.875rem' }}>
              Description
            </label>
            <textarea
              placeholder="Short description of the college..."
              value={form.description}
              onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical' }}
            />
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button type="submit" disabled={saving} style={{ flex: 1 }}>
              {saving ? 'Saving…' : 'Save College'}
            </button>
            <button type="button" className="secondary" onClick={onClose} style={{ flex: 1 }}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ── Modal for creating or editing a user group ── */
const GroupModal = ({ college, initial, allMembers, onSave, onClose }) => {
  const [name, setName] = useState(initial?.name || '');
  const [selectedUsers, setSelectedUsers] = useState(initial?.users?.map(u => typeof u === 'object' ? u._id : u) || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    setError('');
    try {
      await onSave({ name, users: selectedUsers });
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save group');
    } finally {
      setSaving(false);
    }
  };

  const toggleUser = (userId) => {
    setSelectedUsers(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px'
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: 'var(--bg-card)', borderRadius: '16px', padding: '24px',
        width: '100%', maxWidth: '480px',
        boxShadow: 'var(--shadow-xl)', border: '1px solid var(--border-light)',
        animation: 'fadeInUp 0.25s ease',
        position: 'relative'
      }}>
        <button
          type="button" onClick={onClose}
          style={{ 
            position: 'absolute', top: '8px', right: '8px',
            background: 'none', border: 'none', cursor: 'pointer', 
            fontSize: '1.2rem', color: 'var(--text-muted)', padding: '4px' 
          }}
        >✕</button>

        <div style={{ marginBottom: '16px', paddingRight: '32px' }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: 'var(--text-main)' }}>{initial ? 'Edit Group' : 'Create Group'}</h3>
          <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: 'var(--text-muted)' }}>{college.name}</p>
        </div>

        {error && <div className="error-message" style={{ marginBottom: '16px' }}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', color: 'var(--text-main)', fontSize: '0.875rem' }}>
              Group Name <span style={{ color: 'var(--danger)' }}>*</span>
            </label>
            <input
              placeholder="e.g. CS101 Batch A"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              style={{ width: '100%', boxSizing: 'border-box' }}
            />
          </div>
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', color: 'var(--text-main)', fontSize: '0.875rem' }}>
              Members ({selectedUsers.length} selected)
            </label>
            <div style={{ border: '1px solid var(--border-light)', borderRadius: '12px', maxHeight: '240px', overflowY: 'auto', padding: '8px', background: 'var(--bg-main)', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {allMembers.length === 0 ? (
                <div style={{ padding: '20px', color: 'var(--text-muted)', fontSize: '0.9rem', textAlign: 'center' }}>No college members available.</div>
              ) : (
                allMembers.map(member => {
                  const isSelected = selectedUsers.includes(member._id);
                  return (
                    <label key={member._id} style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      padding: '8px 12px', 
                      cursor: 'pointer', 
                      borderRadius: '8px', 
                      background: isSelected ? 'rgba(59, 130, 246, 0.1)' : 'var(--bg-card)', 
                      border: `1px solid ${isSelected ? 'var(--primary)' : 'var(--border-light)'}`, 
                      transition: 'all 0.2s ease'
                    }}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleUser(member._id)}
                        style={{ display: 'none' }}
                      />
                      <div style={{ 
                        width: '32px', height: '32px', borderRadius: '50%', 
                        background: isSelected ? 'var(--primary)' : 'var(--border-light)', 
                        color: isSelected ? 'white' : 'var(--text-muted)', 
                        display: 'flex', alignItems: 'center', justifyContent: 'center', 
                        fontWeight: '700', fontSize: '0.9rem', marginRight: '12px', flexShrink: 0 
                      }}>
                        {member.name.charAt(0).toUpperCase()}
                      </div>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontWeight: '600', fontSize: '0.9rem', color: 'var(--text-main)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{member.name}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{member.email}</div>
                      </div>
                      <div style={{ 
                        width: '20px', height: '20px', borderRadius: '50%', 
                        border: `2px solid ${isSelected ? 'var(--primary)' : 'var(--border-light)'}`, 
                        background: isSelected ? 'var(--primary)' : 'transparent', 
                        display: 'flex', alignItems: 'center', justifyContent: 'center', 
                        flexShrink: 0, transition: 'all 0.2s ease' 
                      }}>
                        {isSelected && <i className="fas fa-check" style={{ color: 'white', fontSize: '0.65rem' }} />}
                      </div>
                    </label>
                  );
                })
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button type="submit" disabled={saving} style={{ flex: 1 }}>
              {saving ? 'Saving…' : 'Save Group'}
            </button>
            <button type="button" className="secondary" onClick={onClose} style={{ flex: 1 }}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ── Main CollegesPage ── */
const CollegesPage = ({ user, onGoToRBAC }) => {
  const canAccessCollegeTools = ['super_user', 'college_admin', 'teacher'].includes(user?.role);
  const [users, setUsers] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [folders, setFolders] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedCollegeId, setSelectedCollegeId] = useState('');
  const [studentEmail, setStudentEmail] = useState('');
  const [folderToAssign, setFolderToAssign] = useState('');
  const [loading, setLoading] = useState(canAccessCollegeTools);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [addUserTarget, setAddUserTarget] = useState(null);
  const [editGroupTarget, setEditGroupTarget] = useState(null);
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [showFolderPicker, setShowFolderPicker] = useState(false);
  const [showFolderAccessModal, setShowFolderAccessModal] = useState(false);
  const [editAccessFolderTarget, setEditAccessFolderTarget] = useState(null);

  // Search & pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (!canAccessCollegeTools) return;

    const loadCollegeTools = async () => {
      try {
        setLoading(true);
        setError('');
        const [usersRes, collegesRes, foldersRes, groupsRes] = await Promise.all([
          api.get('/users'),
          api.get('/colleges'),
          api.get('/folders/editfolders'),
          api.get('/user-groups')
        ]);
        setUsers(usersRes.data.filter(account => visibleToUser(user, account)));
        setColleges(collegesRes.data);
        setFolders(foldersRes.data);
        setGroups(groupsRes.data);
        setSelectedCollegeId(current => current || collegesRes.data[0]?._id || '');
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load college controls');
      } finally {
        setLoading(false);
      }
    };

    loadCollegeTools();
  }, [canAccessCollegeTools, user]);

  // Filtered & paginated colleges
  const filteredColleges = useMemo(() => {
    if (!searchQuery.trim()) return colleges;
    const q = searchQuery.toLowerCase();
    return colleges.filter(c =>
      c.name.toLowerCase().includes(q) ||
      (c.code || '').toLowerCase().includes(q) ||
      (c.description || '').toLowerCase().includes(q)
    );
  }, [colleges, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredColleges.length / PAGE_SIZE));
  const pagedColleges = filteredColleges.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Reset to page 1 when search changes
  useEffect(() => { setCurrentPage(1); }, [searchQuery]);

  const selectedCollege = colleges.find(c => c._id === selectedCollegeId) || colleges[0];
  const collegeMembers = useMemo(() => {
    if (!selectedCollege) return [];
    return users.filter(account => (account.colleges || []).includes(selectedCollege.name));
  }, [selectedCollege, users]);
  const collegeFolders = selectedCollege ? folders.filter(f => f.college === selectedCollege.name) : [];
  const collegeGroups = selectedCollege ? groups.filter(g => g.college === selectedCollege.name) : [];
  const assignableFolders = user?.role === 'super_user' 
    ? folders 
    : folders.filter(f => !f.college || f.college === selectedCollege?.name);

  // Helper to render tree options
  const renderFolderOptions = (foldersList) => {
    const map = {};
    const roots = [];
    foldersList.forEach(f => {
      map[f._id] = { ...f, children: [] };
    });
    foldersList.forEach(f => {
      if (f.parent && map[f.parent]) {
        map[f.parent].children.push(map[f._id]);
      } else {
        roots.push(map[f._id]);
      }
    });

    const options = [];
    const traverse = (node, depth) => {
      const prefix = '\u00A0\u00A0'.repeat(depth * 2);
      const isAssigned = node.college === selectedCollege?.name;
      options.push(
        <option key={node._id} value={node._id} disabled={isAssigned}>
          {prefix}{depth > 0 ? '↳ ' : ''}{node.title}{node.college && node.college !== node.title ? ` (${node.college})` : ''}
        </option>
      );
      node.children.sort((a, b) => a.title.localeCompare(b.title)).forEach(child => traverse(child, depth + 1));
    };

    roots.sort((a, b) => a.title.localeCompare(b.title)).forEach(root => traverse(root, 0));
    return options;
  };

  const handleCreateCollege = async (formData) => {
    const res = await api.post('/colleges', formData);
    setColleges(prev => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
    setSelectedCollegeId(res.data._id);
    setStatus(`${res.data.name} was created.`);
    try {
      const foldersRes = await api.get('/folders/editfolders');
      setFolders(foldersRes.data);
    } catch (_) { /* silent */ }
  };

  const handleSaveFolderAccess = async (folderId, { allowedGroups, allowedUsers }) => {
    const res = await api.put(`/folders/${folderId}`, { allowedGroups, allowedUsers });
    setFolders(prev => prev.map(f => f._id === folderId ? res.data : f));
    setStatus('Folder access updated.');
  };

  const handleSaveEdit = async (formData) => {
    if (!selectedCollege) return;
    const previousName = selectedCollege.name;
    const res = await api.put(`/colleges/${selectedCollege._id}`, formData);
    setColleges(prev =>
      prev.map(c => c._id === res.data._id ? res.data : c)
        .sort((a, b) => a.name.localeCompare(b.name))
    );
    if (previousName !== res.data.name) {
      setFolders(prev =>
        prev.map(f => f.college === previousName
          ? { ...f, college: res.data.name, title: (f.title === previousName && f.isCollegeFolder && !f.parent) ? res.data.name : f.title }
          : f
        )
      );
      setUsers(prev => prev.map(u => (u.colleges || []).includes(previousName) ? { ...u, colleges: [...(u.colleges || []).filter(c => c !== previousName), res.data.name] } : u));
    }
    setStatus(`${res.data.name} was updated.`);
  };

  const handleAddUser = async (college, email, userId, role) => {
    // Step 1: assign to college
    const res = await api.post(`/colleges/${college._id}/students`, { email });
    let updatedUser = res.data;
    // Step 2: update role if it differs
    if (role && role !== res.data.role) {
      const roleRes = await api.put(`/users/${userId}`, { role });
      updatedUser = roleRes.data;
    }
    setUsers(prev => {
      const exists = prev.some(a => a._id === updatedUser._id);
      const next = exists
        ? prev.map(a => a._id === updatedUser._id ? updatedUser : a)
        : [...prev, updatedUser];
      return next.filter(a => visibleToUser(user, a)).sort((a, b) => a.name.localeCompare(b.name));
    });
    setStatus(`${updatedUser.name} was added to ${college.name}${role !== res.data.role ? ` as ${role.replace(/_/g, ' ')}` : ''}.`);
  };

  const handleRemoveUser = async (userId) => {
    if (!selectedCollege || !window.confirm('Remove this user from the college?')) return;
    try {
      setError(''); setStatus('');
      const res = await api.delete(`/colleges/${selectedCollege._id}/users/${userId}`);
      setUsers(prev => {
        const next = prev.map(a => a._id === userId ? res.data : a);
        return next.filter(a => visibleToUser(user, a)).sort((a, b) => a.name.localeCompare(b.name));
      });
      setStatus('Member removed from the college.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove member');
    }
  };

  const handleAssignFolder = async () => {
    if (!selectedCollege || !folderToAssign) return;
    try {
      setError(''); setStatus('');
      const res = await api.post(`/colleges/${selectedCollege._id}/folders`, { folderId: folderToAssign });
      setFolders(prev => prev.map(folder => folder._id === res.data._id ? res.data : folder));
      setFolderToAssign('');
      setStatus(`${res.data.title} was assigned to ${selectedCollege.name}.`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to assign folder');
    }
  };

  const handleUnassignFolder = async (folderId) => {
    if (!selectedCollege) return;
    try {
      setError(''); setStatus('');
      const res = await api.delete(`/colleges/${selectedCollege._id}/folders/${folderId}`);
      setFolders(prev => prev.map(folder => folder._id === res.data._id ? res.data : folder));
      setStatus('Folder was removed from the college.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove folder assignment');
    }
  };

  const handleSaveGroup = async (formData) => {
    if (!selectedCollege) return;
    try {
      if (editGroupTarget) {
        const res = await api.put(`/user-groups/${editGroupTarget._id}`, formData);
        setGroups(prev => prev.map(g => g._id === res.data._id ? res.data : g));
        setStatus(`Group ${res.data.name} updated.`);
      } else {
        const res = await api.post('/user-groups', { ...formData, college: selectedCollege.name });
        setGroups(prev => [...prev, res.data]);
        setStatus(`Group ${res.data.name} created.`);
      }
    } catch (err) {
      throw err;
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Are you sure you want to delete this group?')) return;
    try {
      await api.delete(`/user-groups/${groupId}`);
      setGroups(prev => prev.filter(g => g._id !== groupId));
      setStatus('Group deleted.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete group');
    }
  };

  if (!canAccessCollegeTools) {
    return (
      <div className="page-card">
        <h2>College Management</h2>
        <div className="error-message">You do not have permission to manage college access.</div>
      </div>
    );
  }

  return (
    <div className="page-card rbac-page">
      <SEO title="College Management" description="Manage college entities, members, and resources." />
      {/* Header */}
      <div className="rbac-header">
        <div>
          <h2>Colleges</h2>
          <p>Create colleges, add students, and connect college folders.</p>
        </div>
        <div className="rbac-header-actions">
          {onGoToRBAC && ['super_user', 'college_admin'].includes(user.role) && (
            <button type="button" className="secondary" onClick={onGoToRBAC}>
              Manage Users
            </button>
          )}
          {user.role === 'super_user' && (
            <button type="button" onClick={() => setShowCreateModal(true)}>
              <i className="fas fa-plus" /> New College
            </button>
          )}
          <span className="status-badge status-approved">{roleLabel(user.role)}</span>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {status && <div className="status-message">{status}</div>}

      {loading ? (
        <div className="rbac-empty-state">Loading college controls...</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: '24px', marginTop: '8px' }}>

          {/* ── Left: college list ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* Search */}
            <div style={{ position: 'relative' }}>
              <i className="fas fa-search" style={{
                position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)',
                color: 'var(--text-muted)', pointerEvents: 'none'
              }} />
              <input
                type="text"
                placeholder="Search colleges…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ width: '100%', boxSizing: 'border-box', paddingLeft: '36px' }}
              />
            </div>

            {/* College cards */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {pagedColleges.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '20px 0' }}>
                  No colleges found
                </div>
              ) : (
                pagedColleges.map(college => {
                  const isSelected = selectedCollegeId === college._id;
                  const members = users.filter(u => u.college === college.name);
                  return (
                    <div
                      key={college._id}
                      onClick={() => setSelectedCollegeId(college._id)}
                      style={{
                        padding: '12px 16px',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        border: isSelected ? '2px solid var(--primary)' : '1px solid var(--border-light)',
                        background: isSelected ? 'rgba(79, 70, 229, 0.1)' : 'var(--bg-card)',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <div style={{ fontWeight: '700', color: 'var(--text-main)', fontSize: '0.95rem' }}>
                            {college.name}
                          </div>
                          {college.code && (
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '2px' }}>
                              Code: {college.code}
                            </div>
                          )}
                        </div>
                        <span style={{ fontSize: '0.68rem', fontWeight: '700', padding: '2px 8px',
                          borderRadius: '10px', background: 'rgba(79, 70, 229, 0.1)', color: 'var(--primary)'
                        }}>
                          {members.length} members
                        </span>
                      </div>
                      {college.description && (
                        <p style={{
                          margin: '6px 0 0', fontSize: '0.8rem', color: 'var(--text-muted)', lineHeight: '1.4',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                        }}>
                          {college.description}
                        </p>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', marginTop: '4px' }}>
                <button
                  type="button" className="secondary"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  style={{ padding: '4px 10px', fontSize: '0.8rem' }}
                >
                  ←
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                  <button
                    key={p} type="button"
                    onClick={() => setCurrentPage(p)}
                    style={{
                      padding: '4px 10px', fontSize: '0.8rem',
                      background: currentPage === p ? '#2563eb' : '#f1f5f9',
                      color: currentPage === p ? 'white' : '#374151',
                      border: 'none', borderRadius: '6px', cursor: 'pointer',
                      fontWeight: currentPage === p ? '700' : '400'
                    }}
                  >
                    {p}
                  </button>
                ))}
                <button
                  type="button" className="secondary"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  style={{ padding: '4px 10px', fontSize: '0.8rem' }}
                >
                  →
                </button>
              </div>
            )}
          </div>

          {/* ── Right: college details panel ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {!selectedCollege ? (
              <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>
                Select a college to view details
              </div>
            ) : (
              <>
                {/* College info card */}
                <div className="rbac-college-card">
                  <div className="rbac-college-card-header">
                    <div>
                      <h4 style={{ margin: 0 }}>{selectedCollege.name}</h4>
                      {selectedCollege.code && (
                        <p className="rbac-muted" style={{ margin: '2px 0 0' }}>Code: {selectedCollege.code}</p>
                      )}
                    </div>
                    {user.role === 'super_user' && (
                      <button
                        type="button" className="secondary rbac-edit-btn"
                        onClick={() => setShowEditModal(true)}
                      >
                        ✎ Edit
                      </button>
                    )}
                  </div>
                  {selectedCollege.description && (
                    <p className="rbac-muted" style={{ margin: '8px 0' }}>{selectedCollege.description}</p>
                  )}
                  <div className="rbac-college-stats">
                    <span><strong>{collegeMembers.filter(m => m.role === 'college_admin').length}</strong> Admins</span>
                    <span><strong>{collegeMembers.filter(m => m.role === 'teacher').length}</strong> Teachers</span>
                    <span><strong>{collegeMembers.filter(m => m.role === 'student').length}</strong> Students</span>
                    <span><strong>{collegeFolders.length}</strong> Folders</span>
                    <span><strong>{collegeGroups.length}</strong> Groups</span>
                  </div>
                  <div className="rbac-chip-list" style={{ marginTop: '10px' }}>
                    {collegeMembers.length === 0 ? (
                      <span className="rbac-muted">No members yet</span>
                    ) : (
                      collegeMembers.slice(0, 10).map(member => (
                        <span key={member._id}>{member.name} · {roleLabel(member.role)}</span>
                      ))
                    )}
                  </div>
                </div>

                {/* Add User */}
                <div className="rbac-college-card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <h4 style={{ margin: 0 }}>Members</h4>
                    <button
                      type="button"
                      onClick={() => setAddUserTarget(selectedCollege)}
                      style={{ padding: '6px 14px', borderRadius: '8px', border: 'none',
                        background: 'var(--primary)', color: 'white', cursor: 'pointer',
                        fontSize: '0.8rem', fontWeight: '700' }}
                    >
                      <i className="fas fa-user-plus" style={{ marginRight: '5px' }} />Add User
                    </button>
                  </div>
                  <div className="rbac-chip-list">
                    {collegeMembers.length === 0
                      ? <span className="rbac-muted">No members yet</span>
                      : collegeMembers.slice(0, 10).map(m => (
                          <span key={m._id} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            {m.name} · {roleLabel(m.role)}
                            {['super_user', 'college_admin'].includes(user.role) && (
                              <button
                                type="button" className="rbac-chip-remove"
                                onClick={() => handleRemoveUser(m._id)}
                                title="Remove from college"
                              >&times;</button>
                            )}
                          </span>
                        ))
                    }
                  </div>
                </div>

                {/* College Folders */}
                {(user.role === 'super_user' || user.role === 'college_admin') && (
                  <div className="rbac-college-card">
                    <h4 style={{ margin: '0 0 10px 0' }}>College Folders</h4>
                    {user.role === 'super_user' && (
                      <div className="rbac-inline-control" style={{ marginBottom: '12px' }}>
                        <button 
                          type="button" 
                          className="secondary" 
                          onClick={() => setShowFolderPicker(true)} 
                          style={{ flex: 1, textAlign: 'left', background: 'var(--bg-main)', padding: '8px 12px', border: '1px solid var(--border-light)', borderRadius: '6px', color: 'var(--text-main)' }}
                        >
                          {folderToAssign ? assignableFolders.find(f => f._id === folderToAssign)?.title || 'Choose folder...' : 'Choose folder...'}
                        </button>
                        {folderToAssign && (
                          <button type="button" className="icon-button danger" onClick={() => setFolderToAssign('')} title="Clear selection">
                            <i className="fas fa-times" />
                          </button>
                        )}
                        <button type="button" onClick={handleAssignFolder} disabled={!folderToAssign}>Assign</button>
                      </div>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {collegeFolders.length === 0 ? (
                        <span className="rbac-muted">No folders assigned</span>
                      ) : (
                        collegeFolders.map(folder => (
                          <div key={folder._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'var(--bg-main)', borderRadius: '8px', border: '1px solid var(--border-light)' }}>
                            <div>
                              <div style={{ fontWeight: '600', color: 'var(--text-main)', fontSize: '0.9rem' }}>
                                {folder.title}
                                {!folder.released && <span style={{ marginLeft: '8px', fontSize: '0.7rem', background: 'var(--border-light)', padding: '2px 6px', borderRadius: '4px', color: 'var(--text-muted)' }}>Private</span>}
                              </div>
                              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                {(folder.allowedUsers?.length || 0) + (folder.allowedGroups?.length || 0)} access rules
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button type="button" className="icon-button" onClick={() => { setEditAccessFolderTarget(folder); setShowFolderAccessModal(true); }} title="Manage Access">
                                <i className="fas fa-user-lock" />
                              </button>
                              {user.role === 'super_user' && (
                                <button type="button" className="icon-button danger" onClick={() => handleUnassignFolder(folder._id)} title="Remove from college">
                                  <i className="fas fa-times" />
                                </button>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* User Groups */}
                <div className="rbac-college-card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <h4 style={{ margin: 0 }}>User Groups</h4>
                    {['super_user', 'college_admin', 'teacher'].includes(user.role) && (
                      <button
                        type="button"
                        onClick={() => { setEditGroupTarget(null); setShowCreateGroupModal(true); }}
                        style={{ padding: '6px 14px', borderRadius: '8px', border: 'none',
                          background: 'var(--primary)', color: 'white', cursor: 'pointer',
                          fontSize: '0.8rem', fontWeight: '700' }}
                      >
                        <i className="fas fa-plus" style={{ marginRight: '5px' }} />Create Group
                      </button>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {collegeGroups.length === 0 ? (
                      <span className="rbac-muted">No groups created yet</span>
                    ) : (
                      collegeGroups.map(group => (
                        <div key={group._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'var(--bg-main)', borderRadius: '8px', border: '1px solid var(--border-light)' }}>
                          <div>
                            <div style={{ fontWeight: '600', color: 'var(--text-main)', fontSize: '0.9rem' }}>{group.name}</div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{group.users?.length || 0} members</div>
                          </div>
                          {['super_user', 'college_admin', 'teacher'].includes(user.role) && (
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button type="button" className="icon-button" onClick={() => { setEditGroupTarget(group); setShowCreateGroupModal(true); }}>
                                <i className="fas fa-pencil-alt" />
                              </button>
                              <button type="button" className="icon-button danger" onClick={() => handleDeleteGroup(group._id)}>
                                <i className="fas fa-trash" />
                              </button>
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {addUserTarget && (
        <AddUserModal
          college={addUserTarget}
          currentUserRole={user.role}
          onAdd={handleAddUser}
          onClose={() => setAddUserTarget(null)}
        />
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CollegeModal
          title="Create College"
          onSave={handleCreateCollege}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && selectedCollege && (
        <CollegeModal
          title="Edit College"
          initial={{ name: selectedCollege.name, code: selectedCollege.code || '', description: selectedCollege.description || '' }}
          onSave={handleSaveEdit}
          onClose={() => setShowEditModal(false)}
        />
      )}

      {/* Group Modal */}
      {showCreateGroupModal && selectedCollege && (
        <GroupModal
          college={selectedCollege}
          initial={editGroupTarget}
          allMembers={collegeMembers}
          onSave={handleSaveGroup}
          onClose={() => setShowCreateGroupModal(false)}
        />
      )}

      {/* Folder Picker Modal */}
      {showFolderPicker && (
        <FolderPickerModal
          title="Select Folder to Assign"
          folders={assignableFolders}
          onSelect={(id) => { setFolderToAssign(id); setShowFolderPicker(false); }}
          onClose={() => setShowFolderPicker(false)}
        />
      )}

      {/* Folder Access Modal */}
      {showFolderAccessModal && editAccessFolderTarget && (
        <FolderAccessModal
          folder={editAccessFolderTarget}
          allMembers={collegeMembers}
          allGroups={collegeGroups}
          onSave={handleSaveFolderAccess}
          onClose={() => { setShowFolderAccessModal(false); setEditAccessFolderTarget(null); }}
        />
      )}
    </div>
  );
};

export default CollegesPage;
